// @flow
import {
    AdSlot,
    H,
    Query,
    SeoData,
    SortableTable,
    StaticTable,
    TableGraphConnector,
} from '@haaretz/htz-components';
import { borderBottom, } from '@haaretz/htz-css-tools';
import React, { Fragment, } from 'react';
import { FelaComponent, FelaTheme, } from 'react-fela';

import type { StyleProps, } from '@haaretz/htz-css-tools';

import PageRow from '../../components/PageRow/PageRow';
import RowItem from '../../components/RowItem/RowItem';
import TabbedTable from '../../components/TabbedTable/TabbedTable';
import MainLayout from '../../layouts/MainLayout';
import ExchangeQuery from '../queries/exchangeQuery.graphql';
import useAllAdUnits from '../../components/AdManager/AdUnitsProvider';
import useAdUnitPathTemplate, { createAdSlotProps, } from '../../components/AdManager/useAdUnitPathTemplate';

type Props = {
  url: {
    pathname: string,
    query: {
      section: string,
    },
    asPath: string,
  },
};

const tabsFilters = [
  {
    display: 'ממשלתי',
    controls: 'gov',
    filters: [
      { section: 'type', subSection: 'indices', },
      { section: 'subType', subSection: 'gov', },
    ],
  },
  {
    display: 'קונצרני',
    controls: 'corp',
    filters: [
      { section: 'type', subSection: 'indices', },
      { section: 'subType', subSection: 'corp', },
    ],
  },
  {
    display: 'ענפים',
    controls: 'branch',
    filters: [ { section: 'bondBranchs', }, ],
    headers: [ 'name', 'changePercentage', 'yieldToMaturity', 'avgDuration', ],
  },
  {
    display: 'דירוגים',
    controls: 'rank',
    filters: [ { section: 'bondsRanks', }, ],
    headers: [ 'name', 'changePercentage', 'yieldToMaturity', 'avgDuration', ],
  },
  {
    display: 'כללי',
    controls: 'general',
    filters: [
      { section: 'type', subSection: 'indices', },
      { section: 'subType', subSection: 'general', },
    ],
  },
];

const numToString: (number | string) => string = num => (typeof num === 'number'
  ? num.toLocaleString('he', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  : num);

function Bond({
  url: {
    query: { section, },
    asPath,
  },
}: Props) {
  const adUnits = useAllAdUnits();
const adUnitPathTemplate = useAdUnitPathTemplate();

  return (
    <MainLayout section={section}>
      <SeoData
        title='אג"ח - אגרות חוב - TheMarker Finance'
        description='אגרות חוב: כל המידע על אג"ח, נתונים בזמן אמת, גרפים חדשות ועוד באתר TheMarker Finance'
        path={asPath}
      />
      <FelaTheme>
        {theme => (
          <Fragment>
            <PageRow miscStyles={{ marginBottom: [ { from: 'l', value: '0', }, ], }}>
              <RowItem title="מדדים">
                <FelaComponent
                  style={() => ({
                    extend: [ theme.mq({ from: 'l', }, { display: 'none', }), ],
                  })}
                >
                  <Query
                    query={ExchangeQuery}
                    variables={{
                      ids: [ '602', '605', '690', '700', '701', '800', '709', '601', ],
                    }}
                  >
                    {({ loading, error, data, }) => {
                      if (loading || error) return null;
                      const { assets = [], } = data || {};
                      return (
                        <StaticTable
                          data={assets}
                          linkContent
                          columns={[
                            {
                              title: 'שם נייר',
                              name: 'name',
                              styles: {
                                fontWeight: '700',
                                maxWidth: '17rem',
                                paddingStart: '2rem',
                              },
                              render: value => value,
                            },
                            {
                              title: 'שער אחרון',
                              name: 'value',
                              styles: {
                                paddingStart: '2rem',
                              },
                              render: value => numToString(value),
                            },
                            {
                              title: '% שינוי',
                              percentage: true,
                              name: 'changePercentage',
                              styles: function styles(value: number): StyleProps {
                                return {
                                  ...(value
                                    ? {
                                      color:
                                          value < 0
                                            ? theme.color('negative')
                                            : theme.color('positive'),
                                    }
                                    : {}),
                                  direction: 'ltr',
                                  fontWeight: '700',
                                  paddingStart: '2rem',
                                  position: 'relative',
                                  textAlign: 'start',
                                };
                              },
                              render: value => `
                                ${value > 0 ? '' : value < 0 ? '-' : ''}
                                ${value ? numToString(Math.abs(value)) : value}%
                            `,
                            },
                          ]}
                        />
                      );
                    }}
                  </Query>
                </FelaComponent>
                <TableGraphConnector
                  miscStyles={{ display: [ { until: 'l', value: 'none', }, ], }}
                  assetsId={[ '602', '605', '690', '700', '701', '800', '709', '601', ]}
                />
              </RowItem>
            </PageRow>
            <PageRow
              miscStyles={{
                display: [ { until: 'l', value: 'none', }, ],
                paddingTop: '4rem',
                paddingBottom: '4rem',
                backgroundColor: theme.color('neutral', '-10'),
              }}
            >
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner1'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ display: [ { until: 'l', value: 'none', }, ], }}>
              <RowItem title="מדדי אג״ח">
                <TabbedTable
                  presentation
                  tabs={tabsFilters}
                  panel={({ filters, headers, }) => (
                    <SortableTable
                      fixed={false}
                      headerMiscStyles={{
                        backgroundColor: theme.color('neutral', '-10'),
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                        ...borderBottom('2px', 1, 'solid', theme.color('neutral', '-6')),
                      }}
                      filters={filters}
                      loadMore
                      type="bonds"
                      fragment="
                        name
                        value
                        changePercentage
                        volume
                        yieldToMaturity
                        avgDuration
                      "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'value',
                          display: 'שער',
                          sortingOrder: 'desc',
                          headerStyle: {
                            ...(headers && !headers.includes('value') ? { display: 'none', } : {}),
                          },
                          style: () => ({
                            ...(headers && !headers.includes('value') ? { display: 'none', } : {}),
                          }),
                          value: ({ value, }) => numToString(value),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'desc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                                    ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                                    ${
                                      changePercentage
                                        ? numToString(Math.abs(changePercentage))
                                        : changePercentage
                                    }%
                                  `,
                        },
                        {
                          name: 'volume',
                          display: 'מחזור',
                          sortingOrder: 'desc',
                          headerStyle: {
                            ...(headers && !headers.includes('volume') ? { display: 'none', } : {}),
                          },
                          style: () => ({
                            ...(headers && !headers.includes('volume') ? { display: 'none', } : {}),
                          }),
                          value: ({ volume, }) => numToString(volume),
                        },
                        {
                          name: 'yieldToMaturity',
                          display: 'תשואה לפדיון',
                          sortingOrder: 'desc',
                          style: ({ yieldToMaturity, }) => ({
                            ...(yieldToMaturity
                              ? {
                                color:
                                    yieldToMaturity < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ yieldToMaturity, }) => `
                                    ${yieldToMaturity > 0 ? '' : yieldToMaturity < 0 ? '-' : ''}
                                    ${
                                      yieldToMaturity
                                        ? numToString(Math.abs(yieldToMaturity))
                                        : yieldToMaturity
                                    }%
                                  `,
                        },
                        {
                          name: 'avgDuration',
                          display: 'מח״מ',
                          sortingOrder: 'desc',
                          value: ({ avgDuration, }) => numToString(avgDuration),
                        },
                      ]}
                      initialSort="name"
                      count={11}
                    />
                  )}
                />
              </RowItem>
            </PageRow>
            <PageRow miscStyles={{ display: [ { from: 'l', value: 'none', }, ], }}>
              <RowItem title="נתוני אג״ח" miscStyles={{ marginBottom: '4rem', }}>
                {tabsFilters.map(({ display, controls, filters, }) => (
                  <FelaComponent
                    style={() => ({
                      marginBottom: '4rem',
                    })}
                  >
                    <FelaComponent
                      style={() => ({
                        ...theme.type(-1),
                        fontWeight: '700',
                        backgroundColor: theme.color('neutral', '-5'),
                        paddingStart: '2rem',
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                      })}
                      render={({ className, }) => <H className={className}>{display}</H>}
                    />
                    <SortableTable
                      filters={filters}
                      loadMore
                      disableSort
                      type="bonds"
                      fragment="
                          name
                          value
                          changePercentage
                        "
                      fields={[
                        {
                          name: 'name',
                          display: 'שם',
                          sortingOrder: 'asc',
                          style: () => ({
                            fontWeight: '700',
                            maxWidth: '17rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }),
                          value: ({ name, }) => name,
                        },
                        {
                          name: 'value',
                          display: 'שער',
                          sortingOrder: 'desc',
                          value: ({ value, }) => numToString(value),
                        },
                        {
                          name: 'changePercentage',
                          display: '% שינוי',
                          sortingOrder: 'desc',
                          style: ({ changePercentage, }) => ({
                            ...(changePercentage
                              ? {
                                color:
                                    changePercentage < 0
                                      ? theme.color('negative')
                                      : theme.color('positive'),
                              }
                              : {}),
                            direction: 'ltr',
                            fontWeight: '700',
                            paddingEnd: '2rem',
                            position: 'relative',
                            textAlign: 'start',
                          }),
                          value: ({ changePercentage, }) => `
                              ${changePercentage > 0 ? '' : changePercentage < 0 ? '-' : ''}
                              ${
                                changePercentage
                                  ? numToString(Math.abs(changePercentage))
                                  : changePercentage
                              }%
                          `,
                        },
                      ]}
                      initialSort="changePercentage"
                      count={6}
                    />
                  </FelaComponent>
                ))}
              </RowItem>
            </PageRow>
            <PageRow>
              <RowItem>
                <AdSlot {...createAdSlotProps(adUnits['finance.themarker.com.banner2'], adUnitPathTemplate)} />
              </RowItem>
            </PageRow>
          </Fragment>
        )}
      </FelaTheme>
    </MainLayout>
  );
}

export default Bond;
